import api from './api';

/**
 * License Service
 * Provides methods to interact with the License API endpoints
 */

// License types (must match backend enum values exactly)
const LICENSE_TYPES = {
  SUBSCRIPTION: 'SUBSCRIPTION',
  PERPETUAL: 'PERPETUAL'
};

// License statuses
const LICENSE_STATUS = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  PENDING: 'PENDING',
  SUSPENDED: 'SUSPENDED',
  IN_GRACE_PERIOD: 'IN_GRACE_PERIOD'
};

/**
 * Get all licenses with optional filtering and pagination
 * @param {number} skip - Number of records to skip
 * @param {number} limit - Maximum number of records to return
 * @param {string} [status] - Filter by status (active, warning, expired, deactivated)
 * @param {string} [type] - Filter by type (online, offline)
 * @param {number} [customerId] - Filter by customer ID
 * @param {boolean} [expiresSoon] - Filter licenses expiring soon
 * @param {string} [search] - Search term to filter by license key or customer name/email
 * @returns {Promise<{items: Array, total: number}>} - Object containing items array and total count
 */
const getLicenses = async (
  skip = 0, 
  limit = 10, 
  status, 
  type, 
  customerId, 
  expiresSoon,
  search
) => {
  
  try {
    const params = {
      skip,
      limit,
      ...(status && { status }),
      ...(type && { type }),
      ...(customerId && { customer_id: customerId }),
      ...(expiresSoon !== undefined && { expires_soon: expiresSoon }),
      ...(search && { search })
    };
    const response = await api.get('/licenses/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching licenses:', error);
    throw error;
  }
};


const getLicensesByCustomer = async (
    customerId,
    skip = 0, 
    limit = 10, 
    status, 
    type, 
    expiresSoon,
    search
) => {
    try {
        const params = {
            skip,
            limit,
            ...(status && { status }),
            ...(type && { type }),
            ...(customerId && { customer_id: customerId }),
            ...(expiresSoon !== undefined && { expires_soon: expiresSoon }),
            ...(search && { search })
        };
        const response = await api.get('/licenses/customer/' + customerId, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching licenses:', error);
        throw error;
    }
}


/**
 * Get a single license by ID
 * @param {string} licenseId - The ID of the license to retrieve
 * @returns {Promise<Object>} - License object
 */
const getLicense = async (licenseId) => {
  try {
    const response = await api.get(`/licenses/${licenseId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching license ${licenseId}:`, error);
    throw error;
  }
};


/**
 * Create a new license
 * @param {Object} licenseData - The license data to create
 * @returns {Promise<Object>} - The created license object
 */
const createLicense = async (licenseData) => {
  try {

    // Create a copy of the data without the customer field
    const { customer, ...restData } = licenseData;

    // Ensure license_type is a string and in the correct format
    const licenseType = typeof licenseData.license_type === 'string'
      ? licenseData.license_type.toUpperCase()
      : 'SUBSCRIPTION';

    // Prepare the payload with proper data formatting
    const payload = {
      ...restData,
      license_type: licenseType,
      // Ensure numeric fields are numbers
      customer_id: customer,
      max_companies: licenseData.max_companies ? Number(licenseData.max_companies) : 1,
      max_active_employees: licenseData.max_active_employees ? Number(licenseData.max_active_employees) : 0,
      max_users: licenseData.max_users ? Number(licenseData.max_users) : 0,
      grace_period_days: licenseData.grace_period_days ? Number(licenseData.grace_period_days) : 30,
      // Ensure dates are properly formatted
      activation_date: licenseData.activation_date || new Date().toISOString(),
      expiry_date: licenseData.expiry_date || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
    };

    // Remove any undefined or null values that might cause issues
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined || payload[key] === null) {
        delete payload[key];
      }
    });

    console.log('Creating new license with payload:', payload);
    // Create the license (backend expects customer_id in body)
    const response = await api.post('/licenses/', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating license:', error);
    throw error;
  }
};

/**
 * Update an existing license
 * @param {string} licenseId - The ID of the license to update
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} - The updated license object
 */
const updateLicense = async (licenseId, updateData) => {
  try {
    const { customer, ...restData } = updateData;
    // Ensure license_type is in the correct format (uppercase) if it's being updated
    const payload = {
      ...restData,
      customer_id: customer,
      // Ensure license_type is a string and in the correct format
      ...(updateData.license_type && { 
        license_type: updateData.license_type.toUpperCase() 
      })
    };
    const response = await api.put(`/licenses/${licenseId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating license ${licenseId}:`, error);
    throw error;
  }
};

/**
 * Delete a license (soft delete)
 * @param {string} licenseId - The ID of the license to delete
 * @returns {Promise<Object>} - Object containing success status and message
 * @throws {Error} If the deletion fails
 */
const deleteLicense = async (licenseId) => {
  try {
    console.log(`Attempting to delete license with ID: ${licenseId}`);
    console.log(`API Base URL: ${api.defaults.baseURL}`);
    
    const response = await api.delete(`/licenses/${licenseId}`, {
      validateStatus: (status) => status < 500, // Don't throw for 4xx errors
    });
    
    console.log('Delete response status:', response.status);
    console.log('Delete response data:', response.data);
    
    // Handle successful response (200 OK with success data)
    if (response.status === 200) {
      return {
        success: true,
        message: response.data?.data || 'License deactivated successfully.',
        data: { 
          id: licenseId,
          ...(response.data?.data || {})
        }
      };
    }
    
    // Also handle 204 No Content for backward compatibility
    if (response.status === 204) {
      return {
        success: true,
        message: 'License deactivated successfully.',
        data: { id: licenseId }
      };
    }
    
    // Handle specific error responses
    if (response.status === 404) {
      throw new Error('License not found or you do not have permission to delete it.');
    } else if (response.status === 403) {
      throw new Error('You do not have permission to delete this license.');
    } else if (response.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    } else if (response.status === 500) {
      throw new Error('Authentication required. Please log in again.');
    } else {
      throw new Error(response.data?.detail || `Failed to deactivate license. Status: ${response.status}`);
    }
  } catch (error) {
    console.log("Error deleting license=", error.response.data.detail.reason);
    console.error(`Error deleting license ${licenseId}:`, {
      name: error.name,
      message: error.message,
      stack: error.stack,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data
      } : 'No response',
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        data: error.config?.data
      }
    });
    
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
    } else if (error.response?.status === 0) {
      throw new Error('Unable to connect to the server. The server might be down or there might be a network issue.');
    }
    
    // Safely extract error details
    const errorDetail = error.response?.data?.detail;
    let errorMessage = 'An unexpected error occurred while deactivating the license.';
    
    if (typeof errorDetail === 'string') {
      errorMessage = errorDetail;
    } else if (errorDetail && typeof errorDetail === 'object') {
      // Handle case where detail is an object with a reason property
      if (errorDetail.reason) {
        errorMessage = errorDetail.reason;
      } else if (errorDetail.message) {
        errorMessage = errorDetail.message;
      } else if (errorDetail.error) {
        errorMessage = errorDetail.error;
      }
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
    //console.log("Error deleting license=", errorMessage);
  }
};

/**
 * Validate a license
 * @param {string} licenseKey - The Key of the license to validate
 * @param {string} [macAddress] - The MAC address of the client device (optional)
 * @param {string} [clientVersion] - The version of the client application (optional)
 * @returns {Promise<Object>} - Validation result containing:
 *   - valid: boolean indicating if license is valid
 *   - status: current license status
 *   - message: validation message
 *   - license: license details if valid
 *   - restrictions: any license restrictions
 *   - next_validation: next validation timestamp (if applicable)
 */
const validateLicense = async (licenseKey, macAddress = null, clientVersion = null, termsAndConditions = null) => {
  try {
    const response = await api.post(
      `/licenses/validate/${licenseKey}`,
      null,
      {
        params: {
          ...(macAddress && { mac_address: macAddress }),
          ...(clientVersion && { client_version: clientVersion }),
          //...(termsAndConditions && { terms_and_conditions: termsAndConditions })
        }
      }
    );
    
    if (!response.data) {
      throw new Error('Invalid response format from validation service');
    }

    return response.data;
  } catch (error) {
    console.error(`Error validating license ${licenseKey}:`, error);
    
    // Handle different error response formats
    const errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.message || 
                        'Failed to validate license';
    
    throw new Error(errorMessage);
  }
};

/**
 * Generate and download an offline license file
 * @param {string} licenseId - The ID of the license
 * @returns {Promise<{success: boolean, message: string}>}
 */
const generateOfflineLicense = async (licenseId) => {
  try {
    // Try to download the license file if the endpoint exists
    try {
      const downloadResponse = await api.get(`/licenses/${licenseId}/download`, {
        responseType: 'blob',
        validateStatus: status => status === 200 || status === 404
      });
      
      if (downloadResponse.status === 200) {
        // Create a download link and trigger download
        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `license_${licenseId}.lic`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return { 
          success: true, 
          message: 'License file downloaded successfully' 
        };
      }
    } catch (downloadError) {
      console.warn('License download endpoint not available:', downloadError);
    }
    
    // If download fails or endpoint doesn't exist, try to generate a new license file
    try {
      const generateResponse = await api.post(
        `/licenses/${licenseId}/validate`,
        {
          license_id: licenseId,
          mac_address: null,
          client_version: null
        },
        { validateStatus: status => status === 200 || status === 404 }
      );
      
      if (generateResponse.status === 200 && generateResponse.data?.license_file_path) {
        // Try to download the newly generated file
        try {
          const downloadResponse = await api.get(`/licenses/${licenseId}/download`, {
            responseType: 'blob',
          });
          
          const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `license_${licenseId}.lic`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          return { 
            success: true, 
            message: 'License file generated and downloaded successfully' 
          };
        } catch (e) {
          console.warn('Failed to download generated license:', e);
          return { 
            success: true, 
            message: 'License file generated but download failed. Please contact support.' 
          };
        }
      }
    } catch (generateError) {
      console.warn('License generation endpoint not available:', generateError);
    }
    
    // If we get here, neither download nor generation worked
    throw new Error('Offline license functionality is not available. Please contact support.');
    
  } catch (error) {
    console.error(`Error handling offline license for ${licenseId}:`, error);
    
    const errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.message || 
                        'Failed to process offline license. The required backend endpoints are not available.';
    
    return { 
      success: false, 
      message: errorMessage 
    };
  }
};

/**
 * Bind a MAC address to a license
 * @param {string} licenseId - The ID of the license
 * @param {string} macAddress - The MAC address to bind
 * @returns {Promise<Object>} - The updated license object
 */
const bindMacAddress = async (licenseId, macAddress) => {
  try {
    const response = await api.post(`/licenses/${licenseId}/bind-mac`, { mac_address: macAddress });
    return response.data;
  } catch (error) {
    console.error(`Error binding MAC address to license ${licenseId}:`, error);
    throw error;
  }
};

/**
 * Revoke a license by marking it as deactivated
 * @param {string} licenseId - The ID of the license to revoke
 * @returns {Promise<Object>} - The updated license object
 * @throws {Error} If the revocation fails
 */
const revokeLicense = async (licenseId) => {
  try {
    const response = await api.post(`/licenses/${licenseId}/revoke`);
    return response.data;
  } catch (error) {
    console.error(`Error revoking license ${licenseId}:`, error);
    throw error;
  }
};

/**
 * Get license usage statistics
 * @param {string} licenseId - The ID of the license
 * @returns {Promise<Object>} - License usage statistics
 */
const getLicenseUsage = async (licenseId) => {
  try {
    const response = await api.get(`/licenses/${licenseId}/usage`);
    return response.data;
  } catch (error) {
    console.error(`Error getting usage for license ${licenseId}:`, error);
    throw error;
  }
};
/**
 * Fetches activities for a specific license
 * @param {string} licenseId - ID of the license
 * @param {number} [limit=50] - Maximum number of activities to return
 * @returns {Promise<Array>} Array of activity objects
 * @throws {Error} If there's an error fetching the activities
 */
const getLicenseActivities = async (licenseId, limit = 50) => {
  if (!licenseId) {
    throw new Error('licenseId is required');
  }

  if (typeof limit !== 'number' || limit <= 0) {
    throw new Error('limit must be a positive number');
  }

  try {
    const response = await api.get(`/licenses/${licenseId}/activities`, { 
      params: { limit },
      // Add timeout to prevent hanging
      timeout: 10000 
    });
    console.log("License activities response:", response.data);
    // Return the items array if it exists, otherwise return the data as is
    return Array.isArray(response.data) ? response.data : (response.data.items || []);
  } catch (error) {
    // Only fallback for 404 errors
    if (error.response?.status === 404) {
      console.warn(`Activities endpoint not found, falling back to license data for license ${licenseId}`);
      try {
        const license = await getLicense(licenseId);
        return generateActivitiesFromLicense(license);
      } catch (fallbackError) {
        console.error(`Fallback failed for license ${licenseId}:`, fallbackError);
        throw new Error(`Failed to fetch activities: ${fallbackError.message}`);
      }
    }
    
    // For other errors, include the original error details
    const errorMessage = error.response?.data?.message || error.message;
    console.error(`Error fetching activities for license ${licenseId}:`, errorMessage);
    throw new Error(`Failed to fetch activities: ${errorMessage}`);
  }
};

/**
 * Generate activities from license data (fallback when activities endpoint is not available)
 * @param {Object} license - The license object
 * @returns {Array} - Array of activity objects
 */
const generateActivitiesFromLicense = (license) => {
  const activities = [];
  
  // License creation
  if (license.created_at) {
    activities.push({
      id: 'created',
      type: 'CREATION',
      timestamp: license.created_at,
      details: {
        license_key: license.license_key
      },
      user: null,
      system: true
    });
  }

  // License activation
  if (license.activation_date) {
    activities.push({
      id: 'activated',
      type: 'ACTIVATION',
      timestamp: license.activation_date,
      details: {
        activated_by: license.activated_by?.name || 'System'
      },
      user: license.activated_by,
      system: true
    });
  }

  // MAC address changes
  if (license.mac_address) {
    activities.push({
      id: `mac-${license.mac_address}-${license.last_mac_change || ''}`,
      type: 'MAC_BIND',
      timestamp: license.last_mac_change || license.updated_at,
      details: {
        mac_address: license.mac_address,
        change_count: license.mac_change_count || 0
      },
      user: null, // Could be added if tracked
      system: true
    });
  }

  // License validations
  if (license.last_validation) {
    activities.push({
      id: `validation-${license.last_validation}`,
      type: 'VALIDATION',
      timestamp: license.last_validation,
      details: {
        ip_address: license.last_validation_ip,
        attempt_count: license.validation_attempts || 0
      },
      user: null,
      system: true
    });
  }

  // Status changes
  if (license.status === 'REVOKED' && license.revoked_at) {
    activities.push({
      id: 'revoked',
      type: 'REVOCATION',
      timestamp: license.revoked_at,
      details: {
        reason: license.revocation_reason,
        revoked_by: license.revoked_by?.name
      },
      user: license.revoked_by,
      system: false
    });
  } else if (license.status === 'EXPIRED') {
    activities.push({
      id: 'expired',
      type: 'EXPIRATION',
      timestamp: license.expiry_date,
      details: {
        grace_period_days: license.grace_period_days
      },
      user: null,
      system: true
    });
  } else if (license.status === 'SUSPENDED') {
    activities.push({
      id: 'suspended',
      type: 'SUSPENSION',
      timestamp: license.updated_at,
      details: {},
      user: null,
      system: true
    });
  }

  // Customer assignments
  // if (license.customer_licenses && license.customer_licenses.length > 0) {
  //   license.customer_licenses.forEach((cl, index) => {
  //     activities.push({
  //       id: `customer-${cl.customer_id}-${cl.assigned_at}`,
  //       type: 'CUSTOMER_ASSIGNMENT',
  //       timestamp: cl.assigned_at,
  //       details: {
  //         customer_id: cl.customer_id,
  //         customer_name: cl.customer?.company_name || `Customer ${cl.customer_id}`,
  //         is_primary: cl.is_primary || false
  //       },
  //       user: null, // Could be added if tracked
  //       system: false
  //     });
  //   });
  // }

  // Sort activities by timestamp (newest first)
  return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};
// validation-failures
const getValidationFailures = async (params) => {
  try {
    const response = await api.get('/licenses/validation-failures', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching validation failures:', error);
    throw error;
  }
};

export {
  LICENSE_TYPES,
  LICENSE_STATUS,
  getLicenses,
  getLicenseActivities,
  getLicense,
  createLicense,
  updateLicense,
  deleteLicense,
  validateLicense,
  generateOfflineLicense,
  bindMacAddress,
  getValidationFailures,
  getLicenseUsage,
  revokeLicense,
  getLicensesByCustomer
};
